import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { apiService } from '../../services/api.ts';
import ScheduleCard from '../widgets/ScheduleCard.tsx';
import UserMenu from './UserMenu.tsx';
import './UtilityRail.css';

export type UtilityPanelName = 'calendar' | 'tasks' | 'calculator';

type UtilityRailProps = {
  activePanel: UtilityPanelName | null;
  onPanelChange: (panel: UtilityPanelName | null) => void;
  username?: string;
  onLogout: () => void;
  onShowBusinessCard: () => void;
  onShowProfile: () => void;
};

type TaskItem = {
  item_id: number | string;
  note_title?: string;
  note_text?: string;
  project_name?: string;
  status?: string;
  priority?: string;
  created_at?: string;
};

const PANEL_META: Record<UtilityPanelName, {
  icon: string;
  labelKey: string;
  labelDefault: string;
  eyebrowKey: string;
  eyebrowDefault: string;
}> = {
  calendar: {
    icon: 'bi-calendar3',
    labelKey: 'utility.calendar',
    labelDefault: 'Kalendár',
    eyebrowKey: 'utility.planning',
    eyebrowDefault: 'Plánovanie',
  },
  tasks: {
    icon: 'bi-check2-square',
    labelKey: 'utility.tasks',
    labelDefault: 'Úlohy',
    eyebrowKey: 'utility.focus',
    eyebrowDefault: 'Sústredenie',
  },
  calculator: {
    icon: 'bi-calculator',
    labelKey: 'utility.calculator',
    labelDefault: 'Kalkulačka',
    eyebrowKey: 'utility.tools',
    eyebrowDefault: 'Nástroje',
  },
};

const isDone = (status?: string) => ['done', 'completed', 'complete', 'finished'].includes(
  String(status || '').trim().toLowerCase()
);

const priorityWeight = (priority?: string) => {
  const normalized = String(priority || '').toLowerCase();
  if (normalized.includes('high') || normalized.includes('vysok')) return 0;
  if (normalized.includes('medium') || normalized.includes('stred')) return 1;
  return 2;
};

function TasksPanel({ onClose }: { onClose: () => void }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<TaskItem['item_id'] | null>(null);

  useEffect(() => {
    if (!token) return;

    let isCurrent = true;
    setLoading(true);
    apiService.getTasks(token)
      .then((data: TaskItem[]) => {
        if (isCurrent) setTasks(Array.isArray(data) ? data : []);
      })
      .catch((error: unknown) => {
        console.error('Quick tasks fetch error:', error);
        toast.error(t('utility.tasks_fetch_error', { defaultValue: 'Úlohy sa nepodarilo načítať.' }), {
          position: 'bottom-left',
        });
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [token, t]);

  const orderedTasks = useMemo(() => [...tasks]
    .sort((a, b) => {
      if (isDone(a.status) !== isDone(b.status)) return isDone(a.status) ? 1 : -1;
      const priorityDifference = priorityWeight(a.priority) - priorityWeight(b.priority);
      if (priorityDifference !== 0) return priorityDifference;
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    })
    .slice(0, 14), [tasks]);

  const toggleTask = async (task: TaskItem) => {
    if (!token || savingId !== null) return;

    const previousStatus = task.status;
    const nextStatus = isDone(task.status) ? 'Not started' : 'Done';
    setSavingId(task.item_id);
    setTasks((current) => current.map((item) => (
      item.item_id === task.item_id ? { ...item, status: nextStatus } : item
    )));

    try {
      await apiService.updateTask(token, String(task.item_id), { status: nextStatus });
    } catch (error) {
      console.error('Quick task update error:', error);
      setTasks((current) => current.map((item) => (
        item.item_id === task.item_id ? { ...item, status: previousStatus } : item
      )));
      toast.error(t('utility.task_update_error', { defaultValue: 'Úlohu sa nepodarilo aktualizovať.' }), {
        position: 'bottom-left',
      });
    } finally {
      setSavingId(null);
    }
  };

  const openProjects = () => {
    navigate('/projects');
    onClose();
  };

  if (loading) {
    return <UtilityLoading label={t('utility.loading_tasks', { defaultValue: 'Načítavam úlohy…' })} />;
  }

  return (
    <div className="utility-tasks">
      <div className="utility-panel-summary">
        <strong>{tasks.filter((task) => !isDone(task.status)).length}</strong>
        <span>{t('utility.open_tasks', { defaultValue: 'otvorených úloh' })}</span>
      </div>

      {orderedTasks.length === 0 ? (
        <div className="utility-empty-state">
          <i className="bi bi-check2-circle" aria-hidden="true" />
          <strong>{t('utility.no_tasks', { defaultValue: 'Žiadne úlohy' })}</strong>
          <span>{t('utility.no_tasks_hint', { defaultValue: 'Novú úlohu môžeš pridať v detaile projektu.' })}</span>
        </div>
      ) : (
        <div className="utility-task-list">
          {orderedTasks.map((task) => {
            const completed = isDone(task.status);
            return (
              <label className={`utility-task ${completed ? 'is-done' : ''}`} key={task.item_id}>
                <input
                  type="checkbox"
                  checked={completed}
                  disabled={savingId === task.item_id}
                  onChange={() => toggleTask(task)}
                />
                <span className="utility-task-check" aria-hidden="true">
                  <i className="bi bi-check-lg" />
                </span>
                <span className="utility-task-copy">
                  <strong>{task.note_title || task.note_text || t('utility.untitled_task', { defaultValue: 'Úloha bez názvu' })}</strong>
                  <span>{task.project_name || t('utility.without_project', { defaultValue: 'Bez projektu' })}</span>
                </span>
                {task.priority && <span className="utility-task-priority">{task.priority}</span>}
              </label>
            );
          })}
        </div>
      )}

      <button type="button" className="utility-open-module" onClick={openProjects}>
        <span>{t('utility.open_projects', { defaultValue: 'Spravovať úlohy v projektoch' })}</span>
        <i className="bi bi-arrow-up-right" aria-hidden="true" />
      </button>
    </div>
  );
}

const calculate = (left: number, right: number, operator: string | null) => {
  if (operator === '+') return left + right;
  if (operator === '−') return left - right;
  if (operator === '×') return left * right;
  if (operator === '÷') return right === 0 ? Number.NaN : left / right;
  return right;
};

const formatCalculatorValue = (value: number) => {
  if (!Number.isFinite(value)) return 'Error';
  return String(Number(value.toPrecision(12)));
};

function CalculatorPanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [display, setDisplay] = useState('0');
  const [storedValue, setStoredValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const inputDigit = (digit: string) => {
    if (display === 'Error' || waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
      return;
    }
    if (display.replace('-', '').replace('.', '').length >= 12) return;
    setDisplay(display === '0' ? digit : `${display}${digit}`);
  };

  const inputDecimal = () => {
    if (display === 'Error' || waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (!display.includes('.')) {
      setDisplay(`${display}.`);
    }
  };

  const clear = () => {
    setDisplay('0');
    setStoredValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const chooseOperator = (nextOperator: string) => {
    const inputValue = Number(display);
    if (!Number.isFinite(inputValue)) {
      clear();
      return;
    }

    if (storedValue === null) {
      setStoredValue(inputValue);
    } else if (operator && !waitingForOperand) {
      const result = calculate(storedValue, inputValue, operator);
      setDisplay(formatCalculatorValue(result));
      setStoredValue(result);
    }

    setOperator(nextOperator);
    setWaitingForOperand(true);
  };

  const equals = () => {
    if (storedValue === null || !operator || display === 'Error') return;
    const result = calculate(storedValue, Number(display), operator);
    setDisplay(formatCalculatorValue(result));
    setStoredValue(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const openTools = () => {
    navigate('/calculators-tools');
    onClose();
  };

  const keys = [
    { label: 'C', action: clear, tone: 'muted' },
    { label: '+/−', action: () => setDisplay(formatCalculatorValue(Number(display) * -1)), tone: 'muted' },
    { label: '%', action: () => setDisplay(formatCalculatorValue(Number(display) / 100)), tone: 'muted' },
    { label: '÷', action: () => chooseOperator('÷'), tone: 'accent' },
    ...['7', '8', '9'].map((label) => ({ label, action: () => inputDigit(label) })),
    { label: '×', action: () => chooseOperator('×'), tone: 'accent' },
    ...['4', '5', '6'].map((label) => ({ label, action: () => inputDigit(label) })),
    { label: '−', action: () => chooseOperator('−'), tone: 'accent' },
    ...['1', '2', '3'].map((label) => ({ label, action: () => inputDigit(label) })),
    { label: '+', action: () => chooseOperator('+'), tone: 'accent' },
    { label: '0', action: () => inputDigit('0'), wide: true },
    { label: '.', action: inputDecimal },
    { label: '=', action: equals, tone: 'primary' },
  ];

  return (
    <div className="utility-calculator">
      <div className="utility-calculator-display" aria-live="polite">
        <span>{operator && storedValue !== null ? `${formatCalculatorValue(storedValue)} ${operator}` : '\u00a0'}</span>
        <strong>{display}</strong>
      </div>
      <div className="utility-calculator-grid">
        {keys.map((key, index) => (
          <button
            type="button"
            key={`${key.label}-${index}`}
            className={`${key.tone ? `is-${key.tone}` : ''} ${key.wide ? 'is-wide' : ''}`}
            onClick={key.action}
          >
            {key.label}
          </button>
        ))}
      </div>

      <div className="utility-tools-card">
        <span className="utility-tools-icon"><i className="bi bi-graph-up-arrow" aria-hidden="true" /></span>
        <div>
          <strong>{t('utility.business_calculators', { defaultValue: 'Freelance kalkulačky' })}</strong>
          <span>{t('utility.business_calculators_hint', { defaultValue: 'Sadzba, rozsah projektu a očakávaný príjem.' })}</span>
        </div>
      </div>
      <button type="button" className="utility-open-module" onClick={openTools}>
        <span>{t('utility.open_calculator_tools', { defaultValue: 'Otvoriť Calculator Tools' })}</span>
        <i className="bi bi-arrow-up-right" aria-hidden="true" />
      </button>
    </div>
  );
}

function UtilityLoading({ label }: { label: string }) {
  return (
    <div className="utility-loading" role="status">
      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export default function UtilityRail({
  activePanel,
  onPanelChange,
  username,
  onLogout,
  onShowBusinessCard,
  onShowProfile,
}: UtilityRailProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!activePanel) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onPanelChange(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [activePanel, onPanelChange]);

  const togglePanel = (panel: UtilityPanelName) => {
    onPanelChange(activePanel === panel ? null : panel);
  };

  const openCalendar = () => {
    navigate('/calendar');
    onPanelChange(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    onPanelChange(null);
    onLogout();
    navigate('/login');
  };

  const activeMeta = activePanel ? PANEL_META[activePanel] : null;
  const activeLabel = activeMeta
    ? t(activeMeta.labelKey, { defaultValue: activeMeta.labelDefault })
    : '';

  return (
    <aside className={`utility-shell ${activePanel ? 'is-open' : ''}`} aria-label={t('utility.label', { defaultValue: 'Rýchle nástroje' })}>
      {activePanel && activeMeta && (
        <section className="utility-panel" aria-label={activeLabel}>
          <header className="utility-panel-header">
            <div>
              <span>{t(activeMeta.eyebrowKey, { defaultValue: activeMeta.eyebrowDefault })}</span>
              <h2>{activeLabel}</h2>
            </div>
            <button
              type="button"
              className="utility-panel-close"
              onClick={() => onPanelChange(null)}
              aria-label={t('utility.close', { defaultValue: 'Zavrieť panel' })}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </header>

          <div className="utility-panel-content">
            {activePanel === 'calendar' && (
              <div className="utility-calendar">
                <ScheduleCard maxItems={8} />
                <button type="button" className="utility-open-module" onClick={openCalendar}>
                  <span>{t('utility.open_calendar', { defaultValue: 'Otvoriť celý kalendár' })}</span>
                  <i className="bi bi-arrow-up-right" aria-hidden="true" />
                </button>
              </div>
            )}
            {activePanel === 'tasks' && <TasksPanel onClose={() => onPanelChange(null)} />}
            {activePanel === 'calculator' && <CalculatorPanel onClose={() => onPanelChange(null)} />}
          </div>
        </section>
      )}

      <nav className="utility-rail">
        <UserMenu
          placement="rail"
          username={username}
          onLogout={onLogout}
          onShowBusinessCard={onShowBusinessCard}
          onShowProfile={onShowProfile}
          showLogout={false}
        />
        <div className="utility-rail-actions">
          {(Object.keys(PANEL_META) as UtilityPanelName[]).map((panel) => {
            const meta = PANEL_META[panel];
            const label = t(meta.labelKey, { defaultValue: meta.labelDefault });
            return (
              <button
                type="button"
                key={panel}
                className={activePanel === panel ? 'is-active' : ''}
                onClick={() => togglePanel(panel)}
                aria-pressed={activePanel === panel}
                aria-label={label}
                title={label}
              >
                <i className={`bi ${meta.icon}`} aria-hidden="true" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
        <div className="utility-rail-footer">
          <span className="utility-rail-divider" />
          <button
            type="button"
            className="utility-rail-collapse"
            onClick={() => onPanelChange(null)}
            disabled={!activePanel}
            aria-label={t('utility.collapse', { defaultValue: 'Zbaliť panel' })}
            title={t('utility.collapse', { defaultValue: 'Zbaliť panel' })}
          >
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="utility-rail-logout"
            onClick={handleLogout}
            aria-label={t('logout') || 'Log out'}
            title={t('logout') || 'Log out'}
          >
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </nav>
    </aside>
  );
}
